import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { useShopifyProducts } from "@/context/ShopifyProductsContext";
import type { Product } from "@/types/product";

function ProductCard({ product }: { product: Product }) {
  return (
    <Link
      to={`/shop/${product.id}`}
      className="group flex-shrink-0 w-52 bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-amber-200 transition-all cursor-pointer"
    >
      <div className="w-full h-36 bg-gray-50 overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="p-3">
        <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-0.5">{product.brand}</p>
        <p className="text-sm font-bold text-gray-900 leading-snug line-clamp-2 group-hover:text-amber-600 transition-colors">
          {product.name}
        </p>
        <div className="flex items-center justify-between mt-2">
          <span className="text-sm font-extrabold text-gray-900">${product.price.toLocaleString()}</span>
          {product.inStock ? (
            <span className="text-xs text-emerald-600 font-medium">In Stock</span>
          ) : (
            <span className="text-xs text-gray-400 font-medium">Out of Stock</span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function RecentlyViewedSection() {
  const recentIds = useRecentlyViewed();
  const { getProductById, loading } = useShopifyProducts();

  const recentProducts = useMemo(() => {
    return recentIds
      .map((id) => getProductById(id))
      .filter((p): p is Product => Boolean(p))
      .slice(0, 8);
  }, [recentIds, getProductById]);

  if (loading || recentProducts.length === 0) return null;

  return (
    <section className="py-14 bg-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-amber-50 rounded-xl">
              <i className="ri-history-line text-amber-500 text-lg"></i>
            </div>
            <div>
              <h2 className="text-xl font-extrabold text-gray-900">Recently Viewed</h2>
              <p className="text-xs text-gray-400 mt-0.5">Pick up where you left off</p>
            </div>
          </div>
          <Link
            to="/shop"
            className="text-sm text-amber-600 hover:text-amber-500 font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            View All
            <i className="ri-arrow-right-line text-sm"></i>
          </Link>
        </div>

        {/* Scrollable row */}
        <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
          {recentProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>
    </section>
  );
}
